/**
 * Inventory: what the company owns, and who has it right now.
 *
 * The list is built around the question the office actually asks — "where is it" — so
 * the holder and how overdue it is are columns, not something you open an item to find.
 * Overdue sorts to the top by default for the same reason.
 *
 * ## Selecting rows
 *
 * Labels are printed in batches: a delivery of ten meters arrives, ten stickers go on.
 * Printing the whole register every time is how somebody ends up with four sheets of
 * labels for tools that already have one. Select-all covers what is *shown*, not what
 * exists — with a filter on, the visible rows are the set the person is thinking about.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FileDrop } from '@/components/FileDrop';
import { InventoryLabel } from '@/components/InventoryLabel';
import { InventoryPhoto } from '@/components/InventoryPhoto';
import { Modal } from '@/components/Modal';
import { DataToolbar, NoResults, SortableTh } from '@/components/table';
import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, type InventoryItem, type InventoryStatus } from '@/lib/api';
import { dateShort } from '@/lib/format';
import { categoryLabel } from '@/lib/inventory';
import { useTableState, type SortOption } from '@/lib/table';
import { Combobox } from '@/components/Combobox';

const STATUS_TONE: Record<
  InventoryStatus,
  'neutral' | 'primary' | 'success' | 'warning' | 'danger'
> = {
  available: 'success',
  assigned: 'primary',
  maintenance: 'warning',
  lost: 'danger',
  retired: 'neutral',
};

const STATUSES: InventoryStatus[] = ['available', 'assigned', 'maintenance', 'lost', 'retired'];

/** Accents and case ignored, so "Attrezzi" and "attrezzi" sort next to each other. */
const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

const SORTS: SortOption<InventoryItem>[] = [
  // Overdue first: the only rows on this screen that need somebody to do something.
  {
    key: 'overdue',
    labelKey: 'inventory.overdue',
    value: (i) => i.overdueDays,
    defaultDirection: 'desc',
  },
  { key: 'code', labelKey: 'inventory.code', value: (i) => i.code },
  { key: 'name', labelKey: 'inventory.item', value: (i) => i.name },
  { key: 'status', labelKey: 'common.status', value: (i) => i.status },
  { key: 'holder', labelKey: 'inventory.holder', value: (i) => i.holder?.name ?? null },
];

const EMPTY_ITEM = {
  name: '',
  categoryId: '',
  serialNumber: '',
  manufacturer: '',
  model: '',
  purchasedOn: '',
  purchasePrice: '',
  codeKind: 'qr' as 'qr' | 'barcode',
  externalCode: '',
  description: '',
  notes: '',
};

/**
 * The fields that are worth recording and not worth stopping for.
 *
 * A native `<details>`, so it needs no state, is keyboard-operable for free, and — the
 * reason that matters here — the browser opens it by itself when somebody uses
 * find-in-page to look for a field inside it.
 */
function Details({
  summary,
  hint,
  children,
}: {
  summary: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <details className="group rounded border border-border bg-surface-alt">
      <summary className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none list-none text-sm font-semibold">
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          aria-hidden
          className="shrink-0 text-muted transition-transform rtl:-scale-x-100 group-open:rotate-90 rtl:group-open:-rotate-90"
        >
          <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
        <span>{summary}</span>
        {hint ? (
          <span className="font-normal text-xs text-muted truncate hidden sm:inline">{hint}</span>
        ) : null}
      </summary>
      <div className="px-3 pb-3 pt-1">{children}</div>
    </details>
  );
}

export function InventoryPage() {
  const { t, plural } = useI18n();
  const queryClient = useQueryClient();

  const items = useQuery({ queryKey: ['inventory'], queryFn: api.inventory });
  const categories = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: api.inventoryCategories,
  });

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_ITEM);
  const [photo, setPhoto] = useState<File | null>(null);
  const [labels, setLabels] = useState(false);
  const [status, setStatus] = useState<InventoryStatus | 'all'>('all');
  const [categoryId, setCategoryId] = useState('all');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['inventory'] });
  };

  /* A local preview of the file about to be uploaded, revoked when it is replaced so
     somebody who swaps the photo three times does not leak three blob URLs. */
  const photoPreview = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => {
    if (!photoPreview) return;
    return () => URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  const create = useMutation({
    mutationFn: async () => {
      /*
       * The photo is uploaded FIRST, then the item references it.
       *
       * The other order would need the item to exist before it can be photographed,
       * and a failed upload would leave an item claiming a picture that is not there.
       */
      let photoDocumentId: string | undefined;
      if (photo) {
        const { documentId } = await api.uploadDocument(photo, {
          category: 'other',
          customCategory: t('inventory.photoLabel'),
          visibility: 'office_only',
        });
        photoDocumentId = documentId;
      }

      return api.createInventoryItem({
        name: form.name.trim(),
        categoryId: form.categoryId || undefined,
        serialNumber: form.serialNumber.trim() || undefined,
        manufacturer: form.manufacturer.trim() || undefined,
        model: form.model.trim() || undefined,
        purchasedOn: form.purchasedOn || undefined,
        purchasePrice: form.purchasePrice ? Number(form.purchasePrice) : undefined,
        codeKind: form.codeKind,
        externalCode: form.externalCode.trim() || undefined,
        description: form.description.trim() || undefined,
        notes: form.notes.trim() || undefined,
        photoDocumentId,
      });
    },
    onSuccess: async () => {
      setCreating(false);
      setForm(EMPTY_ITEM);
      setPhoto(null);
      await refresh();
    },
  });

  /*
   * Sorted by what is on screen, not by what is in the column.
   *
   * The server orders by the stored Italian name, so in English the list came out
   * Other, PPE, Ladders, Measuring, Power tools — alphabetical in a language the
   * reader cannot see. Sorting the labels puts it back in the order it looks like it
   * should be in.
   */
  const categoryOptions = useMemo(() => {
    const all = (categories.data?.categories ?? []).map((c) => ({
      id: c.id,
      label: categoryLabel(c, t) ?? '',
    }));
    return all.sort((a, b) => collator.compare(a.label, b.label));
  }, [categories.data, t]);

  /* Status and category narrow the rows before search and sort see them, so the count
     in the toolbar and select-all both mean the same thing as what is on screen. */
  const filtered = useMemo(() => {
    const all = items.data?.items ?? [];
    return all.filter(
      (i) =>
        (status === 'all' || i.status === status) &&
        (categoryId === 'all' || i.category?.id === categoryId),
    );
  }, [items.data, status, categoryId]);

  const table = useTableState<InventoryItem>({
    rows: filtered,
    search: (i) => [i.code, i.name, i.serialNumber, i.manufacturer, i.model, i.holder?.name],
    sorts: SORTS,
    defaultSortKey: 'overdue',
    storageKey: 'inventory',
  });

  const overdueCount = (items.data?.items ?? []).filter((i) => i.overdueDays !== null).length;

  const shown = table.rows;
  const selectedRows = shown.filter((i) => selected.has(i.id));
  const allShownSelected = shown.length > 0 && selectedRows.length === shown.length;

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllShown = () =>
    setSelected((prev) => {
      if (allShownSelected) {
        // Clearing only clears what is shown; a selection made under a different filter
        // is not something the person can see, so it is not something to throw away.
        const next = new Set(prev);
        for (const row of shown) next.delete(row.id);
        return next;
      }
      return new Set([...prev, ...shown.map((r) => r.id)]);
    });

  /* Nothing selected prints everything — the sheet is still explicitly labelled, so it
     is never a surprise which of the two just happened. */
  const sheet = selectedRows.length > 0 ? selectedRows : shown;

  return (
    <>
      <PageHeader
        title={t('inventory.title')}
        subtitle={t('inventory.subtitle')}
        actions={
          <>
            {/* Desktop only: on a phone the bottom bar already has it, raised and in
                the middle, and two scan buttons on one screen is one too many. */}
            <Link to="/inventory/scan" className="btn-primary max-lg:hidden">
              {t('inventory.scan')}
            </Link>
            <button
              type="button"
              className="btn-secondary"
              disabled={shown.length === 0}
              onClick={() => setLabels(true)}
            >
              {selectedRows.length > 0
                ? t('inventory.printSelected', { count: selectedRows.length })
                : t('inventory.printAll')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                create.reset();
                setForm(EMPTY_ITEM);
                setPhoto(null);
                setCreating(true);
              }}
            >
              {t('inventory.addItem')}
            </button>
          </>
        }
      />

      {overdueCount > 0 ? (
        <Card className="mb-4 bg-warning-soft border-[var(--color-warning)]">
          <p className="text-sm">{t('inventory.overdueBanner', { count: overdueCount })}</p>
        </Card>
      ) : null}

      {items.isLoading ? (
        <Spinner />
      ) : items.error ? (
        <ErrorNotice error={items.error} />
      ) : (
        <>
          <DataToolbar
            state={table}
            searchPlaceholder={t('inventory.searchPlaceholder')}
            countOne="inventory.countOne"
            countMany="inventory.countMany"
            extra={
              <>
                <label className="flex items-center gap-2">
                  <span className="sr-only">{t('common.status')}</span>
                  <select
                    className="input w-auto"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as InventoryStatus | 'all')}
                  >
                    <option value="all">{t('inventory.allStatuses')}</option>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`inventory.status_${s}` as 'inventory.status_available')}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex items-center gap-2">
                  <span className="sr-only">{t('inventory.category')}</span>
                  <Combobox
                    className="w-56"
                    value={categoryId}
                    onChange={setCategoryId}
                    options={[
                      { value: 'all', label: t('inventory.allCategories') },
                      ...categoryOptions.map((c) => ({ value: c.id, label: c.label })),
                    ]}
                  />
                </label>
              </>
            }
          />

          {/* Appears only with a selection, and says what it will act on. A permanently
              visible bar reading "0 selected" is a row of dead controls. */}
          {selectedRows.length > 0 ? (
            <div className="flex items-center gap-3 mb-4 px-4 py-2.5 rounded border border-primary bg-[var(--color-primary-soft)]">
              <span className="text-sm font-semibold">
                {plural(selectedRows.length, 'inventory.selectedOne', 'inventory.selectedMany', {
                  count: selectedRows.length,
                })}
              </span>
              <button
                type="button"
                className="text-sm font-semibold text-primary hover:underline"
                onClick={() => setLabels(true)}
              >
                {t('inventory.printSelected', { count: selectedRows.length })}
              </button>
              <button
                type="button"
                className="text-sm text-muted hover:text-text hover:underline ms-auto"
                onClick={() => setSelected(new Set())}
              >
                {t('inventory.clearSelection')}
              </button>
            </div>
          ) : null}

          {shown.length === 0 ? (
            <NoResults
              onClear={() => {
                setStatus('all');
                setCategoryId('all');
                table.clear();
              }}
            />
          ) : (
            <>
              {/*
                Cards below `lg`, the table above it.
                A seven-column table on a 390px screen is a horizontal scrollbar, and a
                horizontal scrollbar hides the column that says who has the thing — the
                one question this screen exists to answer. The card leads with it.
              */}
              <ul className="lg:hidden space-y-2">
                {shown.map((item) => {
                  const isSelected = selected.has(item.id);
                  return (
                    <li key={item.id}>
                      <Card
                        padded={false}
                        className={`flex items-start gap-3 p-3 ${isSelected ? 'border-primary bg-[var(--color-primary-soft)]' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 shrink-0"
                          checked={isSelected}
                          onChange={() => toggleRow(item.id)}
                          aria-label={t('inventory.selectRow', { name: item.name })}
                        />

                        <InventoryPhoto
                          url={item.photoUrl}
                          alt={t('inventory.photoOf', { name: item.name })}
                          size={44}
                        />

                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/inventory/${item.id}`}
                            className="font-semibold hover:underline block truncate"
                          >
                            {item.name}
                          </Link>
                          <div className="text-xs text-subtle tabular">
                            {item.code}
                            {item.category ? ` · ${categoryLabel(item.category, t)}` : ''}
                          </div>

                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            <Badge tone={STATUS_TONE[item.status]}>
                              {t(`inventory.status_${item.status}` as 'inventory.status_available')}
                            </Badge>
                            {item.overdueDays !== null ? (
                              <Badge tone="danger">
                                {t('inventory.overdueBy', { count: item.overdueDays })}
                              </Badge>
                            ) : null}
                          </div>

                          {item.holder ? (
                            <div className="text-sm mt-2">
                              {item.holder.name}
                              {item.dueBackOn ? (
                                <span className="text-xs text-subtle tabular">
                                  {' · '}
                                  {t('inventory.dueBack')}: {dateShort(item.dueBackOn)}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <div className="text-sm text-subtle mt-2">{t('inventory.inStore')}</div>
                          )}
                        </div>
                      </Card>
                    </li>
                  );
                })}
              </ul>

              <Card padded={false} className="max-lg:hidden">
                <div className="table-scroll">
                  <table className="table-wide">
                    <thead>
                      <tr>
                        <th className="th w-10">
                          <input
                            type="checkbox"
                            className="align-middle"
                            checked={allShownSelected}
                            onChange={toggleAllShown}
                            aria-label={t('inventory.selectAll')}
                          />
                        </th>
                        <th className="th w-14" aria-label={t('inventory.photo')} />
                        <SortableTh state={table} sortKey="code">
                          {t('inventory.code')}
                        </SortableTh>
                        <SortableTh state={table} sortKey="name">
                          {t('inventory.item')}
                        </SortableTh>
                        <SortableTh state={table} sortKey="status">
                          {t('common.status')}
                        </SortableTh>
                        <SortableTh state={table} sortKey="holder">
                          {t('inventory.holder')}
                        </SortableTh>
                        <SortableTh state={table} sortKey="overdue">
                          {t('inventory.dueBack')}
                        </SortableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((item) => {
                        const isSelected = selected.has(item.id);
                        return (
                          <tr
                            key={item.id}
                            className={
                              isSelected ? 'bg-[var(--color-primary-soft)]' : 'hover:bg-surface-alt'
                            }
                          >
                            <td className="td">
                              <input
                                type="checkbox"
                                className="align-middle"
                                checked={isSelected}
                                onChange={() => toggleRow(item.id)}
                                aria-label={t('inventory.selectRow', { name: item.name })}
                              />
                            </td>
                            <td className="td">
                              <InventoryPhoto
                                url={item.photoUrl}
                                alt={t('inventory.photoOf', { name: item.name })}
                                size={36}
                              />
                            </td>
                            <td className="td tabular text-sm">
                              <Link
                                to={`/inventory/${item.id}`}
                                className="text-primary font-semibold hover:underline"
                              >
                                {item.code}
                              </Link>
                            </td>
                            <td className="td">
                              <span className="font-semibold">{item.name}</span>
                              {item.category ? (
                                <div className="text-xs text-muted">
                                  {categoryLabel(item.category, t)}
                                </div>
                              ) : null}
                            </td>
                            <td className="td">
                              <Badge tone={STATUS_TONE[item.status]}>
                                {t(
                                  `inventory.status_${item.status}` as 'inventory.status_available',
                                )}
                              </Badge>
                            </td>
                            <td className="td text-sm">
                              {item.holder ? (
                                <>
                                  <span>{item.holder.name}</span>
                                  <div className="text-xs text-subtle">
                                    {t(
                                      item.holder.kind === 'user'
                                        ? 'inventory.holderPerson'
                                        : 'inventory.holderSite',
                                    )}
                                  </div>
                                </>
                              ) : (
                                <span className="text-subtle">—</span>
                              )}
                            </td>
                            <td className="td text-sm whitespace-nowrap">
                              {item.dueBackOn ? (
                                <>
                                  {dateShort(item.dueBackOn)}
                                  {item.overdueDays !== null ? (
                                    <Badge tone="danger">
                                      {t('inventory.overdueBy', { count: item.overdueDays })}
                                    </Badge>
                                  ) : null}
                                </>
                              ) : (
                                <span className="text-subtle">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </>
      )}

      {/* --- Add an item --- */}
      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        size="lg"
        title={t('inventory.addItem')}
        description={t('inventory.addItemHint')}
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={form.name.trim().length < 2 || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? t('common.saving') : t('inventory.addItem')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCreating(false)}>
              {t('common.cancel')}
            </button>
          </>
        }
      >
        {/*
          Five fields and a drawer, not thirteen in a wall.
          What somebody standing over a new tool actually knows is its name, roughly
          what it is, what it looks like, the number stamped on it and which sticker it
          is getting. Everything else — who made it, what it cost, when — is worth
          recording and is not worth blocking the first five on, so it is one click away
          and starts closed.
        */}
        <div className="space-y-4">
          <Field label={t('inventory.item')}>
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t('inventory.namePlaceholder')}
            />
          </Field>

          <Field label={t('inventory.description')}>
            <textarea
              className="input h-16 py-2 resize-none"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t('inventory.descriptionPlaceholder')}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('inventory.serialNumber')} hint={t('inventory.serialHint')}>
              <input
                className="input"
                value={form.serialNumber}
                onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
              />
            </Field>

            <Field label={t('inventory.labelKind')} hint={t('inventory.labelKindHint')}>
              <select
                className="input"
                value={form.codeKind}
                onChange={(e) => setForm({ ...form, codeKind: e.target.value as 'qr' | 'barcode' })}
              >
                <option value="qr">{t('inventory.qr')}</option>
                <option value="barcode">{t('inventory.barcode')}</option>
              </select>
            </Field>
          </div>

          <div>
            <span className="label-text block mb-1.5">{t('inventory.photo')}</span>
            {photo ? (
              /* The picked file is shown, not just named. A filename is not enough to
                 notice you photographed the floor. */
              <div className="flex items-center gap-3 p-3 rounded border border-border bg-surface-alt">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt=""
                    className="w-16 h-16 rounded border border-border object-cover shrink-0"
                  />
                ) : null}
                <span className="text-sm truncate flex-1">{photo.name}</span>
                <button
                  type="button"
                  className="text-sm text-primary hover:underline shrink-0"
                  onClick={() => setPhoto(null)}
                >
                  {t('inventory.removePhoto')}
                </button>
              </div>
            ) : (
              <FileDrop onPick={setPhoto} />
            )}
          </div>

          <Details summary={t('inventory.moreDetails')} hint={t('inventory.moreDetailsHint')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('inventory.category')}>
                <Combobox
                  value={form.categoryId}
                  onChange={(categoryId) => setForm({ ...form, categoryId })}
                  options={[
                    { value: '', label: t('common.none') },
                    ...categoryOptions.map((c) => ({ value: c.id, label: c.label })),
                  ]}
                />
              </Field>

              <Field label={t('inventory.externalCode')} hint={t('inventory.externalCodeHint')}>
                <input
                  className="input"
                  value={form.externalCode}
                  onChange={(e) => setForm({ ...form, externalCode: e.target.value })}
                />
              </Field>

              <Field label={t('inventory.manufacturer')}>
                <input
                  className="input"
                  value={form.manufacturer}
                  onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                />
              </Field>

              <Field label={t('inventory.model')}>
                <input
                  className="input"
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                />
              </Field>

              <Field label={t('inventory.purchasedOn')}>
                <input
                  type="date"
                  className="input"
                  value={form.purchasedOn}
                  onChange={(e) => setForm({ ...form, purchasedOn: e.target.value })}
                />
              </Field>

              <Field label={t('inventory.purchasePrice')} hint={t('inventory.purchasePriceHint')}>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="input tabular"
                  value={form.purchasePrice}
                  onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })}
                />
              </Field>
            </div>
          </Details>
        </div>

        {create.error ? <ErrorNotice error={create.error} /> : null}
      </Modal>

      {/* --- Print sheet --- */}
      <Modal
        open={labels}
        onClose={() => setLabels(false)}
        size="xl"
        title={t('inventory.printLabels')}
        description={
          selectedRows.length > 0
            ? t('inventory.printSheetSelected', { count: selectedRows.length })
            : t('inventory.printSheetAll')
        }
        footer={
          <>
            <button type="button" className="btn-primary" onClick={() => window.print()}>
              {t('inventory.print')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setLabels(false)}>
              {t('common.close')}
            </button>
          </>
        }
      >
        <p className="text-xs text-muted mb-4 leading-relaxed">{t('inventory.printSaveHint')}</p>

        {/*
          A plain grid, sized so a sheet of A4 holds a sensible number. `.print-sheet`
          is what the print stylesheet keeps; everything else on the page is hidden, so
          what comes out is labels and nothing else. The browser's own dialog saves it
          as a PDF, which is why there is no PDF endpoint here — that would be a server
          that has to know about label stock.
        */}
        <div className="print-sheet grid grid-cols-2 sm:grid-cols-4 gap-6">
          {sheet.map((item) => (
            <div
              key={item.id}
              className="flex justify-center p-2 border border-dashed border-border rounded print-plain"
            >
              <InventoryLabel code={item.code} kind={item.codeKind} name={item.name} size={110} />
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}
